import type { ExplorerPane } from 'types/explorer'
import type { Group } from 'types/group'
import type { EventSource, TrackEventOptions } from 'types/ui'

import api from '@disco/api/server'

import { INBOX_TYPES } from '@disco/constants/GroupTypes'
import { PLAYLIST_PANE, TRACK_PANE } from '@disco/constants/PaneTypes'

const ONE_MINUTE = 60 * 1000

/**
 * Sends a track download event to the analytics API.
 *
 * Records when a user downloads a track, including the source and context
 * information for analytics tracking.
 *
 * @param trackId - ID of the track being downloaded
 * @param mediaSource - Source of the download event (e.g., 'in-app', 'embed')
 * @param options - Additional options including share and group context
 *
 * @example
 * ```typescript
 * sendTrackDownloadEvent(123, 'in-app', {
 *   share_id: 456,
 *   group_id: 789
 * })
 * ```
 */
export function sendTrackDownloadEvent(
  trackId: number,
  mediaSource: EventSource,
  options: TrackEventOptions,
) {
  api.sendTrackDownloadEvent(trackId, mediaSource, options)
}

/**
 * Sends a playlist access event to the analytics API.
 *
 * Records when a user accesses a playlist, typically for tracking
 * engagement with shared playlists.
 *
 * @param playlistId - ID of the playlist being accessed
 * @param shareId - ID of the share link used to access the playlist
 * @param groupId - ID of the group context where the access occurred
 *
 * @example
 * ```typescript
 * sendPlaylistAccessEvent(123, 456, 789)
 * ```
 */
export function sendPlaylistAccessEvent(
  playlistId: number,
  shareId: number | undefined,
  groupId: number,
) {
  api.sendPlaylistAccessEvent(playlistId, shareId, groupId)
}

/**
 * Sends a track access event to the analytics API.
 *
 * Records when a user accesses a track, typically for tracking
 * engagement with shared tracks.
 *
 * @param track_id - ID of the track being accessed
 * @param share_id - ID of the share link used to access the track
 * @param group_id - ID of the group context where the access occurred
 *
 * @example
 * ```typescript
 * sendTrackAccessEvent(123, 456, 789)
 * ```
 */
function sendTrackAccessEvent(
  track_id: number,
  share_id: number | undefined,
  group_id: number,
) {
  api.sendTrackAccessEvent(track_id, 'in-app', { share_id, group_id })
}

/**
 * Sends a playlist download event to the analytics API.
 *
 * Records when a user downloads a playlist, including campaign and recipient
 * context for detailed analytics tracking.
 *
 * @param playlistId - ID of the playlist being downloaded
 * @param shareId - ID of the share link used (optional)
 * @param groupId - ID of the group context (optional)
 * @param campaignId - ID of the email campaign if applicable (optional)
 * @param recipientId - ID of the recipient if from campaign (optional)
 * @param clientLibraryId - ID of the client library if applicable (optional)
 *
 * @example
 * ```typescript
 * // Basic playlist download
 * sendPlaylistDownloadEvent(123, 456, 789)
 *
 * // Download from email campaign
 * sendPlaylistDownloadEvent(123, 456, 789, 101, 202, 303)
 * ```
 */
export function sendPlaylistDownloadEvent(
  playlistId: number,
  shareId: number | undefined,
  groupId: number | undefined,
  campaignId?: number | undefined,
  recipientId?: number | undefined,
  clientLibraryId?: number | undefined,
) {
  api.sendPlaylistDownloadEvent(
    playlistId,
    shareId,
    groupId,
    campaignId,
    recipientId,
    clientLibraryId,
  )
}

/**
 * Creates a throttled version of a function that prevents rapid successive calls.
 *
 * Uses the first argument as the throttling key, ensuring that calls with the
 * same first argument are throttled together.
 *
 * @param func - Function to throttle
 * @param wait - Wait time in milliseconds between calls
 * @returns Throttled version of the function
 *
 * @example
 * ```typescript
 * const throttledLog = throttleByFirstArg(console.log, 1000)
 * throttledLog('test') // executes
 * throttledLog('test') // ignored (same key within 1 second)
 * throttledLog('other') // executes (different key)
 * ```
 */
function throttleByFirstArg(func, wait) {
  const throttling = {}
  return function (...args) {
    const key = args[0]
    if (!throttling[key]) {
      func(...args)
      throttling[key] = true
      setTimeout(function () {
        delete throttling[key]
      }, wait)
    }
  }
}

/** Throttled version of sendTrackDownloadEvent (1 minute throttle per track). */
export const sendTrackDownloadEventThrottled: typeof sendTrackDownloadEvent =
  throttleByFirstArg(sendTrackDownloadEvent, ONE_MINUTE)

/** Throttled version of sendPlaylistAccessEvent (1 minute throttle per playlist). */
export const sendPlaylistAccessEventThrottled: typeof sendPlaylistAccessEvent =
  throttleByFirstArg(sendPlaylistAccessEvent, ONE_MINUTE)

/** Throttled version of sendTrackAccessEvent (1 minute throttle per track). */
export const sendTrackAccessEventThrottled: typeof sendTrackAccessEvent =
  throttleByFirstArg(sendTrackAccessEvent, ONE_MINUTE)

/** Throttled version of sendPlaylistDownloadEvent (1 minute throttle per playlist). */
export const sendPlaylistDownloadEventThrottled: typeof sendPlaylistDownloadEvent =
  throttleByFirstArg(sendPlaylistDownloadEvent, ONE_MINUTE)

/**
 * Automatically sends appropriate access events based on pane type and context.
 *
 * Analyzes the explorer pane to determine if access events should be sent.
 * Handles both playlist and track access events for partner-shared content
 * and inbox-specific content.
 *
 * @param pane - The explorer pane being accessed
 * @param allGroups - Array of all available groups for context lookup
 *
 * @example
 * ```typescript
 * // When user opens a shared playlist
 * sendAccessEvent(playlistPane, allGroups)
 * // Automatically sends playlist access event if conditions are met
 *
 * // When user opens a shared track
 * sendAccessEvent(trackPane, allGroups)
 * // Automatically sends track access event if conditions are met
 * ```
 */
export const sendAccessEvent = (pane: ExplorerPane, allGroups: Group[]) => {
  const { type, loaded, groups } = pane
  if (loaded && groups) {
    // Get the group objects from the pane
    const paneGroups = groups.map(groupUrl =>
      // @ts-expect-error requires logic change
      allGroups.find(g => g.url === groupUrl),
    )

    // Detect if its a playlist that is shared with and viewed by a partner
    const sharedGroups = paneGroups.filter(
      group => group?.business_role === 'partner',
    )
    const isViewedByPartner = sharedGroups.length > 0

    const share_id = pane.share ? pane.share.id : undefined

    switch (type) {
      case PLAYLIST_PANE: {
        // Detect if its a playlist that belongs only to inboxes
        const inboxGroups = paneGroups.filter(group => {
          if (group) {
            return INBOX_TYPES.includes(group.group_type)
          } else {
            // group isn't loaded, assume it isn't inbox
            return false
          }
        })
        const belongsOnlyToInboxes =
          inboxGroups.length === groups.length && inboxGroups.length > 0

        if (belongsOnlyToInboxes || isViewedByPartner) {
          const { playlist_id } = pane
          const groupId = inboxGroups[0]?.id ?? sharedGroups[0]?.id
          if (groupId != null && playlist_id != null && share_id != null) {
            sendPlaylistAccessEventThrottled(playlist_id, share_id, groupId)
          }
        }
        break
      }
      case TRACK_PANE:
        if (isViewedByPartner) {
          const track_id = pane.id
          const group_id = sharedGroups[0]?.id
          if (group_id != null && track_id != null && share_id != null) {
            sendTrackAccessEventThrottled(track_id, share_id, group_id)
          }
        }
        break
    }
  }
}
